import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { API } from '../config/api';

import { 
  Music, Mic2, Radio, Camera, Headphones, Building2, 
  ArrowLeft, ArrowRight, Check, Loader2, ExternalLink,
  Instagram, Youtube, Twitter, Globe, Link2
} from 'lucide-react';


// Genre options for artists
const GENRES = [
  { value: 'hip-hop', label: 'Hip-Hop / Rap' },
  { value: 'rnb', label: 'R&B / Soul' },
  { value: 'go-go', label: 'Go-Go' },
  { value: 'pop', label: 'Pop' },
  { value: 'electronic', label: 'Electronic / EDM' },
  { value: 'rock', label: 'Rock / Alternative' },
  { value: 'gospel', label: 'Gospel / Christian' },
  { value: 'jazz', label: 'Jazz' },
  { value: 'reggae', label: 'Reggae / Dancehall' },
  { value: 'latin', label: 'Latin' },
  { value: 'country', label: 'Country' },
  { value: 'other', label: 'Other' }
];

// Neighborhoods in DMV
const NEIGHBORHOODS = [
  'U Street DC', 'Shaw DC', 'Anacostia DC', 'Capitol Hill DC', 'Georgetown DC',
  'Adams Morgan DC', 'Columbia Heights DC', 'Petworth DC', 'Brookland DC',
  'Baltimore', 'Inner Harbor Baltimore', 'Fells Point Baltimore',
  'Silver Spring MD', 'College Park MD', 'Takoma Park MD', 'Bethesda MD',
  'Alexandria VA', 'Arlington VA', 'Falls Church VA', 'Fairfax VA',
  'Other DMV'
];

// Podcast categories
const PODCAST_CATEGORIES = [
  { value: 'music', label: 'Music & Artists' },
  { value: 'culture', label: 'Culture & Lifestyle' },
  { value: 'interviews', label: 'Interviews' },
  { value: 'news', label: 'News & Commentary' },
  { value: 'comedy', label: 'Comedy' },
  { value: 'general', label: 'General' }
];

// Stream types
const STREAM_TYPES = [
  { value: 'interviews', label: 'Interviews' },
  { value: 'listening_sessions', label: 'Listening Sessions' },
  { value: 'panels', label: 'Panel Discussions' },
  { value: 'live_shows', label: 'Live Shows' },
  { value: 'reactions', label: 'Music Reactions' }
];

// Videographer services
const VIDEO_SERVICES = [
  { value: 'music_videos', label: 'Music Videos' },
  { value: 'interviews', label: 'Interview Recording' },
  { value: 'live_events', label: 'Live Event Coverage' },
  { value: 'documentaries', label: 'Documentaries' },
  { value: 'social_content', label: 'Social Media Content' },
  { value: 'promo_videos', label: 'Promotional Videos' }
];

// Engineer specialties
const ENGINEER_SPECIALTIES = [
  { value: 'mixing', label: 'Mixing' },
  { value: 'mastering', label: 'Mastering' },
  { value: 'recording', label: 'Recording' },
  { value: 'production', label: 'Production' },
  { value: 'vocal_engineering', label: 'Vocal Engineering' },
  { value: 'sound_design', label: 'Sound Design' }
];

// DAW options
const DAWS = [
  'Pro Tools', 'Logic Pro', 'Ableton Live', 'FL Studio', 
  'Studio One', 'Cubase', 'Reaper', 'GarageBand'
];

// Studio services
const STUDIO_SERVICES = [
  { value: 'recording', label: 'Recording Sessions' },
  { value: 'mixing', label: 'Mixing' },
  { value: 'mastering', label: 'Mastering' },
  { value: 'production', label: 'Production' },
  { value: 'podcast', label: 'Podcast Recording' },
  { value: 'video', label: 'Video Production' },
  { value: 'rehearsal', label: 'Rehearsal Space' }
];

export default function RoleSetupPage() {
  const { user, token, getAuthHeaders, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const roleParam = searchParams.get('role') || 'artist';
  
  const [currentRole, setCurrentRole] = useState(roleParam);
  const [formData, setFormData] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(1);
  
  // Sub-genre state
  const [availableSubGenres, setAvailableSubGenres] = useState([]);
  const [showCreateSubGenre, setShowCreateSubGenre] = useState(false);
  const [newSubGenreName, setNewSubGenreName] = useState('');
  const [creatingSubGenre, setCreatingSubGenre] = useState(false);

  // Fetch sub-genres for a genre
  const fetchSubGenres = async (genreId) => {
    if (!genreId) return;
    try {
      const response = await fetch(`${API}/reference/sub-genres?parent_genre_id=${genreId}`);
      if (response.ok) {
        const data = await response.json();
        setAvailableSubGenres(data.sub_genres || []);
      }
    } catch (error) {
      console.error('Failed to fetch sub-genres:', error);
    }
  };

  // Create a new sub-genre
  const handleCreateSubGenre = async () => {
    if (!newSubGenreName.trim() || !formData.primary_genre) return;
    
    setCreatingSubGenre(true);
    try {
      const response = await fetch(`${API}/reference/sub-genres`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: newSubGenreName.trim(),
          parent_genre_id: formData.primary_genre
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        const subGenre = data.sub_genre;
        
        // Add to available sub-genres if new
        if (!data.existing) {
          setAvailableSubGenres(prev => [...prev, subGenre]);
          toast.success('Sub-genre created!');
        } else {
          toast.info('Sub-genre already exists');
        }
        
        // Set as selected sub-genre
        updateField('sub_genre', subGenre.name);
        setShowCreateSubGenre(false);
        setNewSubGenreName('');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create sub-genre');
      }
    } catch (error) {
      console.error('Failed to create sub-genre:', error);
      toast.error('Failed to create sub-genre');
    } finally {
      setCreatingSubGenre(false);
    }
  };

  // Fetch sub-genres when primary genre is set on load
  useEffect(() => {
    if (formData.primary_genre) {
      fetchSubGenres(formData.primary_genre);
    }
  }, [formData.primary_genre]);

  // Role configurations
  const roleConfig = {
    artist: {
      icon: Music,
      color: 'secondary',
      title: 'Artist Profile Setup',
      endpoint: '/artists/profile',
      steps: [
        {
          title: 'Music Links',
          description: 'Connect your music from streaming platforms',
          fields: ['spotify_url', 'apple_music_url']
        },
        {
          title: 'About You',
          description: 'Tell fans about yourself',
          fields: ['bio', 'genres', 'neighborhood']
        },
        {
          title: 'Social Links',
          description: 'Required: Instagram + YouTube + 1 more platform',
          fields: ['instagram', 'youtube', 'twitter', 'tiktok', 'website'],
          info: 'All artist profiles require Instagram, YouTube, and at least one additional platform to be published.'
        }
      ]
    },
    podcaster: {
      icon: Mic2,
      color: 'green',
      title: 'Podcaster Profile Setup',
      endpoint: '/podcasters/profile',
      steps: [
        {
          title: 'Podcast Info',
          description: 'Tell us about your show',
          fields: ['show_name', 'category', 'bio']
        },
        {
          title: 'Links & Booking',
          description: 'Where can people find and book you?',
          fields: ['spotify_url', 'apple_podcasts_url', 'youtube_url', 'booking_link']
        }
      ]
    },
    streamer: {
      icon: Radio,
      color: 'red',
      title: 'Streamer Profile Setup',
      endpoint: '/streamers/profile',
      steps: [
        {
          title: 'Stream Info',
          description: 'Tell us about your content',
          fields: ['display_name', 'stream_type', 'bio']
        },
        {
          title: 'Platforms & Booking',
          description: 'Where do you stream?',
          fields: ['youtube_url', 'twitch_url', 'instagram', 'booking_link']
        }
      ]
    },
    studio: {
      icon: Building2,
      color: 'amber',
      title: 'Studio Profile Setup',
      endpoint: '/studios',
      steps: [
        {
          title: 'Studio Info',
          description: 'Tell us about your space',
          fields: ['name', 'description', 'services']
        },
        {
          title: 'Location & Rates',
          description: 'Where are you located?',
          fields: ['address', 'neighborhood', 'hourly_rate', 'booking_link']
        }
      ]
    },
    videographer: {
      icon: Camera,
      color: 'purple',
      title: 'Videographer Profile Setup',
      endpoint: '/videographers/profile',
      steps: [
        {
          title: 'About Your Work',
          description: 'Tell us about your services',
          fields: ['display_name', 'bio', 'services']
        },
        {
          title: 'Portfolio & Booking',
          description: 'Show off your work',
          fields: ['portfolio_url', 'instagram', 'rate_range', 'booking_link']
        }
      ]
    },
    engineer: {
      icon: Headphones,
      color: 'blue',
      title: 'Engineer Profile Setup',
      endpoint: '/engineers/profile',
      steps: [
        {
          title: 'About Your Work',
          description: 'Tell us about your expertise',
          fields: ['display_name', 'bio', 'specialties', 'daw_proficiency']
        },
        {
          title: 'Location & Booking',
          description: 'Where can artists find you?',
          fields: ['location', 'rate_range', 'booking_link']
        }
      ]
    },
    graphic_designer: {
      icon: Camera,
      color: 'pink',
      title: 'Graphic Designer Profile Setup',
      endpoint: '/graphic-designers/profile',
      steps: [
        {
          title: 'About Your Work',
          description: 'Tell us about your design services',
          fields: ['display_name', 'bio', 'services']
        },
        {
          title: 'Portfolio & Booking',
          description: 'Show off your work',
          fields: ['portfolio_url', 'instagram', 'rate_range', 'booking_link']
        }
      ]
    },
    listener: {
      icon: Headphones,
      color: 'gray',
      title: 'Listener Profile Setup',
      endpoint: '/listeners/profile',
      steps: [
        {
          title: 'About You',
          description: 'Tell us about your music taste',
          fields: ['bio', 'genres', 'neighborhood']
        }
      ]
    }
  };

  const config = roleConfig[currentRole] || roleConfig.artist;
  const Icon = config.icon;
  const totalSteps = config.steps.length;
  const currentStepConfig = config.steps[step - 1];

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleArrayField = (field, value) => {
    setFormData(prev => {
      const current = prev[field] || [];
      if (current.includes(value)) {
        return { ...prev, [field]: current.filter(v => v !== value) };
      } else {
        return { ...prev, [field]: [...current, value] };
      }
    });
  };

  const handleNext = () => {
    // Validate required fields for artists on social links step
    if (currentRole === 'artist' && step === 3) {
      if (!formData.instagram) {
        toast.error('Instagram link is required for artist profiles');
        return;
      }
      if (!formData.youtube) {
        toast.error('YouTube link is required for artist profiles');
        return;
      }
      // Check for at least one additional platform
      const hasAdditional = formData.twitter || formData.website || 
        formData.tiktok || formData.facebook || formData.soundcloud;
      if (!hasAdditional) {
        toast.error('At least one additional social platform is required (Twitter, Website, TikTok, etc.)');
        return;
      }
    }
    
    if (step < totalSteps) {
      setStep(step + 1);
    } else {
      handleSubmit();
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      // Prepare the data based on role
      const submitData = { ...formData };
      
      // Handle socials object for artist
      if (currentRole === 'artist') {
        submitData.socials = {
          instagram: formData.instagram || '',
          twitter: formData.twitter || '',
          youtube: formData.youtube || '',
          tiktok: formData.tiktok || '',
          website: formData.website || ''
        };
        delete submitData.instagram;
        delete submitData.twitter;
        delete submitData.youtube;
        delete submitData.tiktok;
        delete submitData.website;
      }

      const response = await fetch(`${API}${config.endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(submitData)
      });

      if (response.ok) {
        toast.success(`${config.title.replace(' Setup', '')} created successfully!`);
        await refreshUser();
        navigate('/dashboard');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create profile');
      }
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (fieldName) => {
    switch (fieldName) {
      // Common fields
      case 'bio':
        return (
          <div className="space-y-2" key={fieldName}>
            <Label>Bio</Label>
            <Textarea
              value={formData.bio || ''}
              onChange={(e) => updateField('bio', e.target.value)}
              placeholder="Tell your story..."
              rows={4}
              data-testid="input-bio"
            />
          </div>
        );

      // Artist fields
      case 'spotify_url':
        return (
          <div className="space-y-2" key={fieldName}>
            <Label className="flex items-center gap-2">
              <span className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
                <ExternalLink className="w-3 h-3 text-white" />
              </span>
              Spotify Artist URL {currentRole === 'artist' && <span className="text-red-500">*</span>}
            </Label>
            <Input
              value={formData.spotify_url || ''}
              onChange={(e) => updateField('spotify_url', e.target.value)}
              placeholder="https://open.spotify.com/artist/..."
              data-testid="input-spotify"
            />
            <p className="text-xs text-muted-foreground">
              Required for artist verification and music links
            </p>
          </div>
        );

      case 'apple_music_url':
        return (
          <div className="space-y-2" key={fieldName}>
            <Label className="flex items-center gap-2">
              <span className="w-5 h-5 rounded-full bg-pink-500 flex items-center justify-center">
                <ExternalLink className="w-3 h-3 text-white" />
              </span>
              Apple Music URL
            </Label>
            <Input
              value={formData.apple_music_url || ''}
              onChange={(e) => updateField('apple_music_url', e.target.value)}
              placeholder="https://music.apple.com/artist/..."
              data-testid="input-apple-music"
            />
          </div>
        );

      case 'genres':
        return (
          <div className="space-y-4" key={fieldName}>
            {/* Primary Genre */}
            <div className="space-y-2">
              <Label>Primary Genre <span className="text-red-500">*</span></Label>
              <Select
                value={formData.primary_genre || ''}
                onValueChange={(v) => {
                  updateField('primary_genre', v);
                  // Reset sub-genre when genre changes
                  updateField('sub_genre', '');
                  // Fetch sub-genres for this genre
                  fetchSubGenres(v);
                }}
              >
                <SelectTrigger data-testid="select-genre">
                  <SelectValue placeholder="Select your primary genre" />
                </SelectTrigger>
                <SelectContent>
                  {GENRES.map(g => (
                    <SelectItem key={g.value} value={g.value}>{g.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Sub-Genre */}
            {formData.primary_genre && (
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  Sub-Genre
                  <span className="text-xs text-muted-foreground">(optional)</span>
                </Label>
                {!showCreateSubGenre ? (
                  <div className="flex gap-2">
                    <Select
                      value={formData.sub_genre || ''}
                      onValueChange={(v) => {
                        if (v === '__create_new__') {
                          setShowCreateSubGenre(true);
                        } else {
                          updateField('sub_genre', v);
                        }
                      }}
                    >
                      <SelectTrigger data-testid="select-sub-genre" className="flex-1">
                        <SelectValue placeholder="Select or create sub-genre" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__create_new__" className="text-primary">
                          + Create New Sub-Genre
                        </SelectItem>
                        {availableSubGenres.map(sg => (
                          <SelectItem key={sg.sub_genre_id} value={sg.name}>
                            {sg.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <Input
                        value={newSubGenreName}
                        onChange={(e) => setNewSubGenreName(e.target.value)}
                        placeholder="Enter new sub-genre name"
                        maxLength={50}
                        data-testid="input-new-sub-genre"
                        className="flex-1"
                      />
                      <Button 
                        type="button" 
                        onClick={handleCreateSubGenre}
                        disabled={!newSubGenreName.trim() || creatingSubGenre}
                        size="sm"
                      >
                        {creatingSubGenre ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create'}
                      </Button>
                      <Button 
                        type="button" 
                        variant="ghost" 
                        onClick={() => {
                          setShowCreateSubGenre(false);
                          setNewSubGenreName('');
                        }}
                        size="sm"
                      >
                        Cancel
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Creating sub-genre under: {formData.primary_genre}
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        );

      case 'neighborhood':
        return (
          <div className="space-y-2" key={fieldName}>
            <Label>Neighborhood</Label>
            <Select
              value={formData.neighborhood || ''}
              onValueChange={(v) => updateField('neighborhood', v)}
            >
              <SelectTrigger data-testid="select-neighborhood">
                <SelectValue placeholder="Select your area" />
              </SelectTrigger>
              <SelectContent>
                {NEIGHBORHOODS.map(n => (
                  <SelectItem key={n} value={n}>{n}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );

      // Social links
      case 'instagram':
        return (
          <div className="space-y-2" key={fieldName}>
            <Label className="flex items-center gap-2">
              <Instagram className="w-4 h-4 text-pink-500" /> Instagram
              {currentRole === 'artist' && <span className="text-red-500">*</span>}
            </Label>
            <Input
              value={formData.instagram || ''}
              onChange={(e) => updateField('instagram', e.target.value)}
              placeholder="https://instagram.com/yourhandle"
              data-testid="input-instagram"
              className={currentRole === 'artist' && !formData.instagram ? 'border-amber-500/50' : ''}
            />
            {currentRole === 'artist' && (
              <p className="text-xs text-muted-foreground">Required for artist profile verification</p>
            )}
          </div>
        );

      case 'twitter':
        return (
          <div className="space-y-2" key={fieldName}>
            <Label className="flex items-center gap-2">
              <Twitter className="w-4 h-4 text-blue-400" /> Twitter / X
              {currentRole === 'artist' && <span className="text-xs text-muted-foreground ml-1">(counts as additional)</span>}
            </Label>
            <Input
              value={formData.twitter || ''}
              onChange={(e) => updateField('twitter', e.target.value)}
              placeholder="https://twitter.com/yourhandle"
              data-testid="input-twitter"
            />
          </div>
        );

      case 'youtube':
      case 'youtube_url':
        return (
          <div className="space-y-2" key={fieldName}>
            <Label className="flex items-center gap-2">
              <Youtube className="w-4 h-4 text-red-500" /> YouTube
              {currentRole === 'artist' && fieldName === 'youtube' && <span className="text-red-500">*</span>}
            </Label>
            <Input
              value={formData[fieldName] || ''}
              onChange={(e) => updateField(fieldName, e.target.value)}
              placeholder="https://youtube.com/@yourchannel"
              data-testid="input-youtube"
              className={currentRole === 'artist' && fieldName === 'youtube' && !formData[fieldName] ? 'border-amber-500/50' : ''}
            />
            {currentRole === 'artist' && fieldName === 'youtube' && (
              <p className="text-xs text-muted-foreground">Required for artist profile verification</p>
            )}
          </div>
        );

      case 'website':
        return (
          <div className="space-y-2" key={fieldName}>
            <Label className="flex items-center gap-2">
              <Globe className="w-4 h-4 text-muted-foreground" /> Website
              {currentRole === 'artist' && <span className="text-xs text-muted-foreground ml-1">(counts as additional)</span>}
            </Label>
            <Input
              value={formData.website || ''}
              onChange={(e) => updateField('website', e.target.value)}
              placeholder="https://yourwebsite.com"
              data-testid="input-website"
            />
          </div>
        );

      case 'tiktok':
        return (
          <div className="space-y-2" key={fieldName}>
            <Label className="flex items-center gap-2">
              <span className="w-4 h-4 rounded bg-black flex items-center justify-center text-foreground text-xs font-bold">T</span>
              TikTok
              {currentRole === 'artist' && <span className="text-xs text-muted-foreground ml-1">(counts as additional)</span>}
            </Label>
            <Input
              value={formData.tiktok || ''}
              onChange={(e) => updateField('tiktok', e.target.value)}
              placeholder="https://tiktok.com/@yourhandle"
              data-testid="input-tiktok"
            />
          </div>
        );

      // Podcaster fields
      case 'show_name':
        return (
          <div className="space-y-2" key={fieldName}>
            <Label>Podcast Name <span className="text-red-500">*</span></Label>
            <Input
              value={formData.show_name || ''}
              onChange={(e) => updateField('show_name', e.target.value)}
              placeholder="Your podcast name"
              data-testid="input-show-name"
            />
          </div>
        );

      case 'category':
        return (
          <div className="space-y-2" key={fieldName}>
            <Label>Category</Label>
            <Select
              value={formData.category || ''}
              onValueChange={(v) => updateField('category', v)}
            >
              <SelectTrigger data-testid="select-category">
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                {PODCAST_CATEGORIES.map(c => (
                  <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );

      case 'apple_podcasts_url':
        return (
          <div className="space-y-2" key={fieldName}>
            <Label>Apple Podcasts URL</Label>
            <Input
              value={formData.apple_podcasts_url || ''}
              onChange={(e) => updateField('apple_podcasts_url', e.target.value)}
              placeholder="https://podcasts.apple.com/..."
              data-testid="input-apple-podcasts"
            />
          </div>
        );

      // Streamer fields
      case 'display_name':
        return (
          <div className="space-y-2" key={fieldName}>
            <Label>Display Name <span className="text-red-500">*</span></Label>
            <Input
              value={formData.display_name || ''}
              onChange={(e) => updateField('display_name', e.target.value)}
              placeholder="Your name or brand"
              data-testid="input-display-name"
            />
          </div>
        );

      case 'stream_type':
        return (
          <div className="space-y-2" key={fieldName}>
            <Label>Primary Content Type</Label>
            <Select
              value={formData.stream_type || ''}
              onValueChange={(v) => updateField('stream_type', v)}
            >
              <SelectTrigger data-testid="select-stream-type">
                <SelectValue placeholder="Select content type" />
              </SelectTrigger>
              <SelectContent>
                {STREAM_TYPES.map(s => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );

      case 'twitch_url':
        return (
          <div className="space-y-2" key={fieldName}>
            <Label className="flex items-center gap-2">
              <span className="w-4 h-4 rounded bg-purple-500" /> Twitch
            </Label>
            <Input
              value={formData.twitch_url || ''}
              onChange={(e) => updateField('twitch_url', e.target.value)}
              placeholder="https://twitch.tv/yourhandle"
              data-testid="input-twitch"
            />
          </div>
        );

      // Studio fields
      case 'name':
        return (
          <div className="space-y-2" key={fieldName}>
            <Label>Studio Name <span className="text-red-500">*</span></Label>
            <Input
              value={formData.name || ''}
              onChange={(e) => updateField('name', e.target.value)}
              placeholder="Your studio name"
              data-testid="input-studio-name"
            />
          </div>
        );

      case 'description':
        return (
          <div className="space-y-2" key={fieldName}>
            <Label>Description</Label>
            <Textarea
              value={formData.description || ''}
              onChange={(e) => updateField('description', e.target.value)}
              placeholder="Describe your studio, equipment, and vibe..."
              rows={4}
              data-testid="input-description"
            />
          </div>
        );

      case 'address':
        return (
          <div className="space-y-2" key={fieldName}>
            <Label>Address <span className="text-red-500">*</span></Label>
            <Input
              value={formData.address || ''}
              onChange={(e) => updateField('address', e.target.value)}
              placeholder="Full address (shown to booked clients only)"
              data-testid="input-address"
            />
          </div>
        );

      case 'hourly_rate':
        return (
          <div className="space-y-2" key={fieldName}>
            <Label>Hourly Rate ($)</Label>
            <Input
              type="number"
              value={formData.hourly_rate || ''}
              onChange={(e) => updateField('hourly_rate', parseInt(e.target.value) || '')}
              placeholder="50"
              data-testid="input-hourly-rate"
            />
          </div>
        );

      case 'services':
        const serviceOptions = currentRole === 'studio' ? STUDIO_SERVICES : VIDEO_SERVICES;
        return (
          <div className="space-y-2" key={fieldName}>
            <Label>Services Offered</Label>
            <div className="grid grid-cols-2 gap-2">
              {serviceOptions.map(service => (
                <label key={service.value} className="flex items-center gap-2 cursor-pointer p-2 rounded border border-border hover:bg-muted/30">
                  <Checkbox
                    checked={(formData.services || []).includes(service.value)}
                    onCheckedChange={() => toggleArrayField('services', service.value)}
                  />
                  <span className="text-sm">{service.label}</span>
                </label>
              ))}
            </div>
          </div>
        );

      // Videographer fields
      case 'portfolio_url':
        return (
          <div className="space-y-2" key={fieldName}>
            <Label className="flex items-center gap-2">
              <Link2 className="w-4 h-4" /> Portfolio URL
            </Label>
            <Input
              value={formData.portfolio_url || ''}
              onChange={(e) => updateField('portfolio_url', e.target.value)}
              placeholder="https://yourportfolio.com"
              data-testid="input-portfolio"
            />
          </div>
        );

      case 'rate_range':
        return (
          <div className="space-y-2" key={fieldName}>
            <Label>Rate Range</Label>
            <Input
              value={formData.rate_range || ''}
              onChange={(e) => updateField('rate_range', e.target.value)}
              placeholder="$500-2000 per project"
              data-testid="input-rate-range"
            />
          </div>
        );

      // Engineer fields
      case 'specialties':
        return (
          <div className="space-y-2" key={fieldName}>
            <Label>Specialties</Label>
            <div className="grid grid-cols-2 gap-2">
              {ENGINEER_SPECIALTIES.map(spec => (
                <label key={spec.value} className="flex items-center gap-2 cursor-pointer p-2 rounded border border-border hover:bg-muted/30">
                  <Checkbox
                    checked={(formData.specialties || []).includes(spec.value)}
                    onCheckedChange={() => toggleArrayField('specialties', spec.value)}
                  />
                  <span className="text-sm">{spec.label}</span>
                </label>
              ))}
            </div>
          </div>
        );

      case 'daw_proficiency':
        return (
          <div className="space-y-2" key={fieldName}>
            <Label>DAW Proficiency</Label>
            <div className="grid grid-cols-2 gap-2">
              {DAWS.map(daw => (
                <label key={daw} className="flex items-center gap-2 cursor-pointer p-2 rounded border border-border hover:bg-muted/30">
                  <Checkbox
                    checked={(formData.daw_proficiency || []).includes(daw)}
                    onCheckedChange={() => toggleArrayField('daw_proficiency', daw)}
                  />
                  <span className="text-sm">{daw}</span>
                </label>
              ))}
            </div>
          </div>
        );

      case 'location':
        return (
          <div className="space-y-2" key={fieldName}>
            <Label>Location</Label>
            <Select
              value={formData.location || ''}
              onValueChange={(v) => updateField('location', v)}
            >
              <SelectTrigger data-testid="select-location">
                <SelectValue placeholder="Select your area" />
              </SelectTrigger>
              <SelectContent>
                {NEIGHBORHOODS.map(n => (
                  <SelectItem key={n} value={n}>{n}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );

      // Common booking link
      case 'booking_link':
        return (
          <div className="space-y-2" key={fieldName}>
            <Label>Booking Link</Label>
            <Input
              value={formData.booking_link || ''}
              onChange={(e) => updateField('booking_link', e.target.value)}
              placeholder="https://calendly.com/yourname"
              data-testid="input-booking"
            />
            <p className="text-xs text-muted-foreground">
              Calendly, Cal.com, or any booking page
            </p>
          </div>
        );

      default:
        return null;
    }
  };

  const getColorClass = (color) => {
    const colors = {
      secondary: 'border-secondary bg-secondary/10 text-secondary',
      green: 'border-green-500 bg-green-500/10 text-green-400',
      red: 'border-red-500 bg-red-500/10 text-red-400',
      amber: 'border-amber-500 bg-amber-500/10 text-amber-400',
      purple: 'border-purple-500 bg-purple-500/10 text-purple-400',
      blue: 'border-blue-500 bg-blue-500/10 text-blue-400'
    };
    return colors[color] || colors.secondary;
  };

  return (
    <div className="min-h-screen py-12 px-4" data-testid="role-setup-page">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className={`w-16 h-16 rounded-xl mx-auto mb-4 flex items-center justify-center ${getColorClass(config.color)}`}>
            <Icon className="w-8 h-8" />
          </div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold uppercase mb-2">
            {config.title}
          </h1>
          <p className="text-muted-foreground">
            Step {step} of {totalSteps}: {currentStepConfig.title}
          </p>
        </div>

        {/* Progress Bar */}
        <div className="flex gap-2 mb-8">
          {config.steps.map((_, index) => (
            <div
              key={index}
              className={`flex-1 h-2 rounded-full transition-all ${
                index < step ? `bg-${config.color === 'secondary' ? 'secondary' : config.color + '-500'}` :
                index === step - 1 ? `bg-${config.color === 'secondary' ? 'secondary' : config.color + '-500'}/50` :
                'bg-muted/50'
              }`}
            />
          ))}
        </div>

        {/* Form Card */}
        <Card className="glass-card border-border p-6">
          <div className="mb-6">
            <h2 className="font-heading text-xl font-bold mb-1">{currentStepConfig.title}</h2>
            <p className="text-sm text-muted-foreground">{currentStepConfig.description}</p>
          </div>

          {/* Requirements Info Box */}
          {currentStepConfig.info && (
            <div className="mb-6 p-4 rounded-lg bg-secondary/10 border border-secondary/30">
              <p className="text-sm text-secondary flex items-start gap-2">
                <span className="text-secondary font-bold">ℹ️</span>
                {currentStepConfig.info}
              </p>
            </div>
          )}

          <div className="space-y-4">
            {currentStepConfig.fields.map(field => renderField(field))}
          </div>

          {/* Navigation */}
          <div className="flex justify-between mt-8">
            <Button
              variant="outline"
              onClick={handleBack}
              disabled={step === 1}
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>

            <Button
              onClick={handleNext}
              disabled={submitting}
              className={`bg-${config.color === 'secondary' ? 'secondary' : config.color + '-600'} hover:bg-${config.color === 'secondary' ? 'secondary/90' : config.color + '-700'}`}
              data-testid="next-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...
                </>
              ) : step === totalSteps ? (
                <>
                  <Check className="w-4 h-4 mr-2" /> Complete Setup
                </>
              ) : (
                <>
                  Next <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        </Card>

        {/* Skip Link */}
        <div className="text-center mt-6">
          <Button
            variant="link"
            className="text-muted-foreground"
            onClick={() => navigate('/dashboard')}
            data-testid="skip-btn"
          >
            Skip for now, I will complete this later
          </Button>
        </div>
      </div>
    </div>
  );
}
